// Interview Prep's planner: the day-by-day calendar, worked out from the
// user's skills, their time, the problem bank and the OAs on their cards.
// Plain code, no AI, and nothing here touches Firestore: the page and Home
// call buildPlan with what they loaded.
//
// Days that have already been shown (today and earlier) are frozen by the
// caller (users/{uid}/prepDays/{day}) so a list never moves under the user;
// only the days after are rebuilt. Items the user moved or acted on ahead of
// time are pinned to their day (users/{uid}/prepItems/{id}).
//
// Everyday prep fills normal days from the role profile. An OA takes a growing
// share of each day as it gets close (see oaMode), then hands the time back.
// The user's own goals (courses, articles, certifications) take up to half of
// a normal day, paced to finish by their target date (see goalItems).
// A topic waits until the user is ready in the topics before it (see locked).

import { ROLE_PROFILES, type PrepLevel, type RoleProfile } from "../data/prep/roleProfiles";
import { BUILD_SESSIONS, BUILD_SYSTEMS } from "../data/prep/buildPractice";
import {
  CODING_TOPICS,
  PREREQUISITES,
  STORY_TOPICS,
  topicName,
  type CodingTopicId,
  type Difficulty,
  type Problem,
  type TopicId,
} from "../data/prep/topics";
import {
  addDays,
  daysBetween,
  STORY_START,
  weekday,
  type PracticeResult,
  type Skills,
  type Weekday,
} from "./prepSkills";

export type PrepSettings = {
  /** Minutes the user has on each day of the week; 0 = a free day */
  dayMinutes: Record<Weekday, number>;
  level: PrepLevel;
  /** Premium-only LeetCode problems are left out without it */
  leetcodePremium: boolean;
  /** Topics the user started early from settings, so they're never held back */
  unlocked?: CodingTopicId[];
};

export type PlanItemKind = "problem" | "story" | "mock" | "review" | "platform" | "oa" | "debrief" | "goal" | "build";

export type PlanItem = {
  /** Stable once planned: `${day}:${slug}`, `${day}:mock:${eventId}`, ... (kept when moved) */
  id: string;
  kind: PlanItemKind;
  title: string;
  minutes: number;
  /** The "Why these" line */
  reason: string;
  /** The same without naming the topic, for users who hide topics (a problem's topic gives its approach away) */
  blindReason?: string;
  topic?: TopicId;
  slug?: string;
  difficulty?: Difficulty;
  /** Part of this OA's prep */
  eventId?: string;
  /** A goal step: the goal (users/{uid}/careerGoals/{goalId}), its title, and the step */
  goalId?: string;
  goal?: string;
  stepId?: string;
};

/** One of the user's goals, with the steps still to do, in order */
export type PlanGoal = {
  id: string;
  title: string;
  steps: { id: string; title: string }[];
  /** yyyy-mm-dd to be done by */
  target?: string;
  /** How long one step takes */
  stepMinutes: number;
};

/**
 * What kind of OA it is, which shapes the prep: CodeSignal's General Coding
 * Assessment (4 problems, 70 min, easy to hard), its Industry Coding Framework
 * (one system built in 4 levels, 90 min), or anything else.
 */
export type OaKind = "gca" | "icf" | "other";

/** An OA being prepped for: the card, while it's at OA and not "I'll prep myself" */
export type PrepEvent = {
  id: string;
  company: string;
  /** yyyy-mm-dd */
  due: string;
  /** The email (or the user) gave the date; otherwise it's a guess */
  dueKnown: boolean;
  /** Left out = "other" */
  kind?: OaKind;
  /** The day it was taken, once it's over (the card moved on); takeDay uses it */
  takenOn?: string;
  /** What earlier candidates here got stuck on, most often first (companyDebriefs.crowdFor) */
  crowd?: { people: number; topics: CodingTopicId[] };
  debriefed?: boolean;
};

/** The latest tap on each problem, by slug */
export type PracticeLog = Record<string, { result: PracticeResult; day: string }>;

/** Minutes per difficulty for this user */
export type Pace = Record<Difficulty, number>;

export type PlanInput = {
  /** yyyy-mm-dd, local */
  today: string;
  /** How many days to plan, from today */
  horizon: number;
  settings: PrepSettings;
  skills: Skills;
  problems: Problem[];
  practice: PracticeLog;
  /** From paceFrom; the defaults when left out */
  pace?: Pace;
  /** Days already shown, as they were shown */
  frozen: Record<string, PlanItem[]>;
  /** Items moved to another day (`from`: the day it was moved off), or acted on ahead of time */
  pinned: { day: string; item: PlanItem; from?: string }[];
  events: PrepEvent[];
  /** The user's goals; left out = none */
  goals?: PlanGoal[];
  /**
   * Items the user didn't do on purpose, by id: skipped (`back` false, a
   * problem they never want again) or "Not now" (`back` true, it comes back
   * LATER_DAYS after the day it was on). Neither counts as practice.
   */
  setAside?: { id: string; back: boolean }[];
  /** Ids of items checked off, logged or not; a past day's problem that isn't comes back */
  done?: string[];
};

/** yyyy-mm-dd → that day's items, in order */
export type Plan = Record<string, PlanItem[]>;

/** How long a problem takes until the user's own pace is known (see paceFrom) */
export const MINUTES: Record<Difficulty, number> = { Easy: 20, Medium: 35, Hard: 50 };
/** The user's pace: their median of the last PACE_SAMPLES timed problems, once there are PACE_MIN */
const PACE_SAMPLES = 10;
const PACE_MIN = 3;
/** Times outside this are a tab left open or a typo, not a pace */
const PACE_RANGE = [5, 120];
const PACE_CLAMP = [10, 90];
const STORY_MINUTES = 15;
const MOCK_MINUTES = 90;
/** The General Coding Assessment's length */
const GCA_MINUTES = 70;
/** One build session: two levels of a system */
const BUILD_MINUTES = 45;
/** Share of an OA's practice that goes to what earlier candidates there got stuck on */
const CROWD_SHARE = 0.6;
const REVIEW_MINUTES = 20;
const PLATFORM_MINUTES = 10;
/** The day before an OA: a short warm-up, not a full day */
const WARMUP_MINUTES = 45;
/** Problems on the weakest common topic on each of the last two days */
const WEAKEST_PICKS = 2;
const OA_MINUTES = 90;
const DEBRIEF_MINUTES = 5;
/** A problem put off with "Not now" can be planned again this many days after the day it was on */
export const LATER_DAYS = 2;
/** Below this a topic is a weak spot: easier problems, and the extra pool opens */
export const WEAK_SCORE = 0.4;
/** A topic at or above this weight "comes up a lot" */
const COMMON_WEIGHT = 0.6;
const EXTRA_POOL_SCORE = 0.5;
/** A topic's prerequisites need this much before it starts, unless the user is already this good at it */
const READY_SCORE = 0.5;
/** A missed problem comes back this many days later */
const RETRY_AFTER_DAYS = 5;
/** One story every this many days */
const STORY_EVERY_DAYS = 7;
/** What one planned problem is assumed to add, so the next days move on to other topics */
const EXPECTED_GAIN = 0.03;
const STORY_ROTATION = 0.2;
/** A topic planned this recently counts for less, so days don't repeat one topic */
const RECENT_DAYS = 2;
const RECENT_FACTOR = 0.5;
/** A day can run over its minutes by this much rather than drop an item */
const SLACK = 5;
/** Goals get at most this share of a normal day (one step always fits if the day has room for it) */
const GOAL_SHARE = 0.5;
/** A goal with no target date gets a step every this many days with time */
const GOAL_EVERY_DAYS = 3;
/** Steps per day with time for a goal past its target date */
const GOAL_LATE_PACE = 0.5;
/**
 * A day with at least this many minutes isn't all new problems: each goal can
 * get a step ahead of its pace, a story comes more often, there's a review,
 * and more missed problems come back.
 */
export const LONG_DAY = 240;
/** On a long day, a story if the last one was at least this many days ago */
const LONG_STORY_DAYS = 3;
/** Missed problems that can come back in one long day (one on other days) */
const LONG_RETRIES = 3;

const LEVEL_LABEL: Record<PrepLevel, string> = { intern: "intern", newgrad: "new grad", mid: "mid-level", senior: "senior" };

const round5 = (n: number) => Math.round(n / 5) * 5;

/** "Fri, Oct 23" */
const dayLabel = (day: string) =>
  new Date(`${day}T12:00:00`).toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });

const article = (word: string) => (/^[aeiou]/i.test(word) ? `an ${word}` : `a ${word}`);

/** The minutes the user has on a day */
export function minutesOn(settings: PrepSettings, day: string): number {
  return Math.max(0, settings.dayMinutes[weekday(day)] ?? 0);
}

/** One timed practice tap: how long it took */
export type TimedResult = { day: string; difficulty: Difficulty; minutes?: number };

/**
 * How long this user takes per difficulty: the median of their last 10 timed
 * problems, rounded to 5 minutes. A difficulty with fewer than 3 keeps the default.
 */
export function paceFrom(results: TimedResult[]): Pace {
  const pace = { ...MINUTES };
  const latestFirst = [...results].sort((a, b) => b.day.localeCompare(a.day));
  for (const difficulty of Object.keys(MINUTES) as Difficulty[]) {
    const times = latestFirst
      .filter((r) => r.difficulty === difficulty && r.minutes !== undefined)
      .map((r) => r.minutes!)
      .filter((m) => m >= PACE_RANGE[0] && m <= PACE_RANGE[1])
      .slice(0, PACE_SAMPLES)
      .sort((a, b) => a - b);
    if (times.length < PACE_MIN) continue;
    const mid = times.length / 2;
    const median = times.length % 2 ? times[Math.floor(mid)] : (times[mid - 1] + times[mid]) / 2;
    pace[difficulty] = Math.min(PACE_CLAMP[1], Math.max(PACE_CLAMP[0], round5(median)));
  }
  return pace;
}

// --- OAs -------------------------------------------------------------------------

export type OaMode = "ramp" | "focus" | "light" | "oa" | "after";

/** The day to take it: the day before a known deadline, else the guessed due day */
export function takeDay(event: PrepEvent, today: string): string {
  if (event.takenOn) return event.takenOn;
  const before = addDays(event.due, -1);
  return event.dueKnown && before >= today ? before : event.due;
}

/** What drives a day, by days left until the OA is taken */
export function oaMode(daysLeft: number): OaMode {
  if (daysLeft < 0) return "after";
  if (daysLeft === 0) return "oa";
  if (daysLeft <= 2) return "light";
  if (daysLeft <= 6) return "focus";
  return "ramp";
}

/** Share of a day's prep left to everyday practice */
export const EVERYDAY_SHARE: Record<OaMode, number> = { ramp: 0.4, focus: 0.2, light: 0, oa: 0, after: 1 };

/** The timed mock, shaped like the OA it's for. `built`: build sessions so far */
function mockFor(event: PrepEvent, built: number): Pick<PlanItem, "title" | "minutes" | "reason"> {
  if (event.kind === "gca") {
    return {
      title: "Timed mock: 4 problems in 70 min",
      minutes: GCA_MINUTES,
      reason: `Same shape as the ${event.company} OA: 4 problems, easy to hard, in 70 minutes`,
    };
  }
  if (event.kind === "icf") {
    // A system they haven't practiced yet, when there is one
    const system = BUILD_SYSTEMS[Math.ceil(built / 2) % BUILD_SYSTEMS.length];
    return {
      title: `Timed build: ${system.name}`,
      minutes: MOCK_MINUTES,
      reason: `All four levels in 90 minutes, like the ${event.company} OA`,
    };
  }
  return {
    title: "Timed mock OA",
    minutes: MOCK_MINUTES,
    reason: `Same length as the ${event.company} OA, so the real one feels familiar`,
  };
}

/** The timed mock: 3 days out, or the next earlier day that isn't free */
function mockDay(event: PrepEvent, input: PlanInput): string | null {
  const take = takeDay(event, input.today);
  for (let left = 3; left <= 6; left++) {
    const day = addDays(take, -left);
    if (day < input.today) return null;
    if (minutesOn(input.settings, day) > 0) return day;
  }
  return null;
}

// --- Picking ---------------------------------------------------------------------

const DIFFICULTY_RANK: Record<Difficulty, number> = { Easy: 0, Medium: 1, Hard: 2 };

/**
 * Below `easy` a topic gets Easy problems, below `hard` Medium, then Hard.
 * The higher the level, the sooner Hard starts and the less Easy there is;
 * interns get Hard once they're strong in a topic.
 */
const DIFFICULTY_AT: Record<PrepLevel, { easy: number; hard: number }> = {
  intern: { easy: WEAK_SCORE, hard: 0.75 },
  newgrad: { easy: WEAK_SCORE, hard: 0.7 },
  mid: { easy: 0.3, hard: 0.6 },
  senior: { easy: 0.25, hard: 0.5 },
};

export function targetDifficulty(score: number, level: PrepLevel): Difficulty {
  const at = DIFFICULTY_AT[level];
  if (score < at.easy) return "Easy";
  if (score < at.hard) return "Medium";
  return "Hard";
}

/** How much practicing a topic is worth now: mostly the gap to the bar, a little past it */
function priority(topic: TopicId, skills: Skills, profile: RoleProfile): number {
  const target = profile[topic];
  if (!target) return 0;
  const score = skills[topic];
  return target.weight * Math.max(0, target.bar - score) + 0.1 * target.weight * (1 - score);
}

/** priority, lowered for a topic planned in the last couple of days */
function rank(p: Planner, topic: TopicId, day: string): number {
  const last = p.lastPlanned.get(topic);
  const recent = last !== undefined && last < day && daysBetween(last, day) <= RECENT_DAYS;
  return priority(topic, p.sim, p.profile) * (recent ? RECENT_FACTOR : 1);
}

type Planner = {
  input: PlanInput;
  profile: RoleProfile;
  /** Skills as if the planned items were done */
  sim: Skills;
  /** Slugs already planned somewhere */
  used: Set<string>;
  /** Problems put off with "Not now": slug → the first day they can come back */
  notBefore: Map<string, string>;
  /** Minutes per difficulty: the user's pace */
  minutes: Pace;
  /** Days that already have a missed problem coming back */
  retried: Set<string>;
  /** The last day each topic was planned */
  lastPlanned: Map<TopicId, string>;
  problemIndex: Map<string, number>;
  /** Topics held back when the plan starts → what they waited on; the first problem in one says it's next up */
  heldBack: Map<TopicId, CodingTopicId[]>;
  /** Problems left undone on a past day: slug → that day. They're free to plan again */
  leftUndone: Map<string, string>;
};

/**
 * The topics before `topic` the user isn't ready in yet, when they're weak in
 * it too; empty when it's good to go
 */
export function waitingOn(topic: CodingTopicId, skills: Skills): CodingTopicId[] {
  if (skills[topic] >= READY_SCORE) return [];
  // The whole chain back, not just the topics right before: weak in Hashing holds Trees back too
  const before = new Set<CodingTopicId>();
  const walk = (t: CodingTopicId) => {
    for (const pre of PREREQUISITES[t] ?? []) {
      if (before.has(pre)) continue;
      before.add(pre);
      walk(pre);
    }
  };
  walk(topic);
  return CODING_TOPICS.map((t) => t.id as CodingTopicId).filter((t) => before.has(t) && skills[t] < READY_SCORE);
}

/**
 * Held back: weak in the topic and in one of the topics before it, so it
 * waits until those are solid. An OA keeps the topics it's likely to ask open.
 */
function locked(p: Planner, topic: CodingTopicId, event?: PrepEvent): boolean {
  if (p.input.settings.unlocked?.includes(topic) || !waitingOn(topic, p.sim).length) return false;
  if (!event) return true;
  return (p.profile[topic]?.weight ?? 0) < COMMON_WEIGHT && !event.crowd?.topics.includes(topic);
}

const listNames = (ids: TopicId[]) => {
  const names = ids.map(topicName);
  return names.length > 1 ? `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}` : names[0];
};

/** Not planned anywhere yet, and not put off past `day` */
const isFree = (p: Planner, slug: string, day: string) =>
  !p.used.has(slug) && (p.notBefore.get(slug) ?? "") <= day;

const isRetry = (p: Planner, pr: Problem, day: string) => {
  const log = p.input.practice[pr.slug];
  return log?.result === "missed" && daysBetween(log.day, day) >= RETRY_AFTER_DAYS;
};

/** The longest-waiting missed problem that's due again, fits, and is in one of `topics` */
function dueRetry(p: Planner, day: string, maxMinutes: number, topics: TopicId[]): Problem | null {
  const due = p.input.problems.filter(
    (pr) =>
      topics.includes(pr.topic) &&
      isFree(p, pr.slug, day) &&
      p.minutes[pr.difficulty] <= maxMinutes + SLACK &&
      isRetry(p, pr, day)
  );
  due.sort((a, b) => p.input.practice[a.slug].day.localeCompare(p.input.practice[b.slug].day));
  return due[0] ?? null;
}

/** The best problem in a topic that fits, or null */
function pickProblem(p: Planner, topic: CodingTopicId, day: string, maxMinutes: number): Problem | null {
  const { settings, practice, problems } = p.input;
  const score = p.sim[topic];
  const target = DIFFICULTY_RANK[targetDifficulty(score, settings.level)];
  const retry = (pr: Problem) => isRetry(p, pr, day);
  const open = (pr: Problem) => !practice[pr.slug] || retry(pr);
  const pool = problems.filter(
    (pr) =>
      pr.topic === topic &&
      (!pr.premium || settings.leetcodePremium) &&
      isFree(p, pr.slug, day) &&
      p.minutes[pr.difficulty] <= maxMinutes + SLACK &&
      open(pr)
  );
  // The rest of the bank is for weak topics, or once the NeetCode 150 runs out
  const allowExtra = score < EXTRA_POOL_SCORE || !pool.some((pr) => pr.nc150);
  const candidates = pool.filter((pr) => pr.nc150 || allowExtra || retry(pr));
  const tier = (pr: Problem) => (pr.nc150 ? 0 : pr.nc250 ? 1 : 2);
  const order = (pr: Problem) => p.problemIndex.get(pr.slug) ?? 0;
  candidates.sort(
    (a, b) =>
      Number(retry(b)) - Number(retry(a)) ||
      Math.abs(DIFFICULTY_RANK[a.difficulty] - target) - Math.abs(DIFFICULTY_RANK[b.difficulty] - target) ||
      tier(a) - tier(b) ||
      order(a) - order(b)
  );
  return candidates[0] ?? null;
}

/**
 * Why this problem. `named` false leaves the topic's name out, for users who'd
 * rather not know a problem's topic going in: shorter, and empty when there's
 * nothing worth saying without it.
 */
function problemReason(p: Planner, pr: Problem, day: string, event?: PrepEvent, named = true): string {
  const name = topicName(pr.topic);
  const score = p.sim[pr.topic];
  const target = p.profile[pr.topic];
  const log = p.input.practice[pr.slug];
  if (log?.result === "missed") return `${pr.title}: you missed it ${daysBetween(log.day, day)} days ago, try it again`;
  if (event?.crowd?.topics.includes(pr.topic)) {
    const { people } = event.crowd;
    const who = `${people === 1 ? "Someone" : `${people} people`} who took the ${event.company} OA`;
    return named ? `${who} got stuck on ${name}` : `${who} got stuck here`;
  }
  if (event) {
    const left = daysBetween(day, takeDay(event, p.input.today));
    const when = `${event.company} OA in ${left} ${left === 1 ? "day" : "days"}`;
    return named ? `${when} · ${name} comes up often in OAs` : when;
  }
  const undone = p.leftUndone.get(pr.slug);
  if (undone) return `${pr.title}: back from ${dayLabel(undone)}, you didn't get to it`;
  const waited = p.heldBack.get(pr.topic);
  if (waited) {
    const before = listNames(waited);
    return named ? `${name}: next up, now that ${before} ${before.includes(" and ") ? "are" : "is"} solid` : "Next up, now that you're ready for it";
  }
  if (score < WEAK_SCORE) return named ? `${name}: one of your weakest topics` : "Your weak spot";
  if (target && score < target.bar) {
    const often = target.weight >= COMMON_WEIGHT;
    if (!named) return often ? "Your weak spot, and it comes up a lot" : "Your weak spot";
    return `${name}: below the ${LEVEL_LABEL[p.input.settings.level]} bar${often ? ", and it comes up a lot" : ""}`;
  }
  return named ? `${name}: keeping it fresh` : "";
}

/**
 * Fill up to `budget` minutes with problems, best topic first, no topic twice
 * in a day. `topics` limits which topics count (an OA's last week).
 */
function fillProblems(
  p: Planner,
  day: string,
  budget: number,
  dayTopics: Set<TopicId>,
  topics: CodingTopicId[],
  event?: PrepEvent,
  retries = 1
): PlanItem[] {
  const items: PlanItem[] = [];
  let left = budget;
  // Missed problems that are due again go first, whatever their topic
  let retry = p.retried.has(day) ? 0 : retries;
  while (left >= Math.min(...Object.values(p.minutes)) - SLACK) {
    const ranked = topics.filter((t) => !dayTopics.has(t) && !locked(p, t, event)).sort((a, b) => rank(p, b, day) - rank(p, a, day));
    let picked: Problem | null = null;
    if (retry > 0) {
      picked = dueRetry(p, day, left, ranked);
      retry = picked ? retry - 1 : 0;
      if (picked) p.retried.add(day);
    }
    for (const topic of picked ? [] : ranked) {
      picked = pickProblem(p, topic, day, left);
      if (picked) break;
    }
    if (!picked) break;
    items.push(placeProblem(p, picked, day, event));
    dayTopics.add(picked.topic);
    left -= p.minutes[picked.difficulty];
  }
  return items;
}

/** A picked problem as a plan item, marked as taken and practiced ahead */
function placeProblem(
  p: Planner,
  picked: Problem,
  day: string,
  event?: PrepEvent,
  why?: { reason: string; blindReason: string }
): PlanItem {
  p.used.add(picked.slug);
  p.lastPlanned.set(picked.topic, day);
  const item: PlanItem = {
    id: `${day}:${picked.slug}`,
    kind: "problem",
    title: picked.title,
    minutes: p.minutes[picked.difficulty],
    reason: why?.reason ?? problemReason(p, picked, day, event),
    blindReason: why?.blindReason ?? problemReason(p, picked, day, event, false),
    topic: picked.topic,
    slug: picked.slug,
    difficulty: picked.difficulty,
    ...(event ? { eventId: event.id } : {}),
  };
  p.sim[picked.topic] += EXPECTED_GAIN;
  p.heldBack.delete(picked.topic);
  return item;
}

const spent = (items: PlanItem[]) => items.reduce((sum, i) => sum + i.minutes, 0);

function storyItem(p: Planner, day: string): PlanItem {
  const [topic] = STORY_TOPICS.map((t) => t.id).sort(
    (a, b) => priority(b, p.sim, p.profile) - priority(a, p.sim, p.profile)
  );
  const name = topicName(topic);
  const fresh = p.input.skills[topic] <= STORY_START;
  p.sim[topic] += STORY_ROTATION;
  return {
    id: `${day}:story:${topic}`,
    kind: "story",
    title: `Write your ${name.toLowerCase()} story`,
    minutes: STORY_MINUTES,
    reason: fresh
      ? `Behavioral: you don't have ${article(name.toLowerCase())} story yet`
      : `Behavioral: keep your ${name.toLowerCase()} story ready`,
    topic,
  };
}

// --- Goals -----------------------------------------------------------------------

/** The goal step's item; its id doesn't change with the day, so a step is planned once */
export const goalItemId = (goalId: string, stepId: string) => `goal:${goalId}:${stepId}`;

type GoalPace = { goal: PlanGoal; next: number; perDay: number; owed: number };

/** Steps per day with time, to finish by the target date */
function goalPace(goal: PlanGoal, steps: number, input: PlanInput): number {
  if (!goal.target) return 1 / GOAL_EVERY_DAYS;
  // Past its target: catch up steadily rather than all at once
  if (goal.target < input.today) return GOAL_LATE_PACE;
  let days = 0;
  for (let day = input.today; day <= goal.target && days < 365; day = addDays(day, 1)) {
    if (minutesOn(input.settings, day) > 0) days++;
  }
  return days ? steps / days : steps;
}

/**
 * This day's goal steps, at most `budget` minutes (one step always fits in
 * `room`). `ahead`: a long day, so each goal can do one step more than its
 * pace asks; one it doesn't use isn't owed later.
 */
function goalItems(paces: GoalPace[], day: string, budget: number, room: number, ahead = false): PlanItem[] {
  const items: PlanItem[] = [];
  let left = budget;
  const due = new Map(paces.map((pace) => [pace, pace.owed + pace.perDay]));
  for (const pace of paces) pace.owed += pace.perDay + (ahead ? 1 : 0);
  // The goal most behind goes first
  const order = [...paces].sort((a, b) => b.owed - a.owed);
  for (const pace of order) {
    const { goal } = pace;
    while (pace.owed >= 1 && pace.next < goal.steps.length) {
      const fits = goal.stepMinutes <= left + SLACK || (!items.length && goal.stepMinutes <= room + SLACK);
      if (!fits) break;
      const step = goal.steps[pace.next];
      const after = goal.steps.length - pace.next - 1;
      items.push({
        id: goalItemId(goal.id, step.id),
        kind: "goal",
        title: step.title,
        minutes: goal.stepMinutes,
        reason: goalReason(goal, after, day),
        goalId: goal.id,
        goal: goal.title,
        stepId: step.id,
      });
      pace.next++;
      pace.owed -= 1;
      left -= goal.stepMinutes;
    }
    // A step ahead that didn't happen isn't owed
    if (ahead) pace.owed = Math.min(pace.owed, due.get(pace)!);
    // Nothing left to plan: it stops asking
    if (pace.next >= goal.steps.length) pace.owed = 0;
  }
  return items;
}

function goalReason(goal: PlanGoal, after: number, day: string): string {
  const rest = after === 0 ? "last step" : `${after} ${after === 1 ? "step" : "steps"} after this`;
  if (!goal.target) return `${goal.title}: ${rest}`;
  if (day > goal.target) return `${goal.title}: past its ${dayLabel(goal.target)} target, ${rest}`;
  return `${goal.title}: ${rest}, done by ${dayLabel(goal.target)}`;
}

// --- The plan --------------------------------------------------------------------

/** Every day from today for `horizon` days */
export function buildPlan(input: PlanInput): Plan {
  const { today, horizon, settings, frozen, pinned, events } = input;
  const p: Planner = {
    input,
    profile: ROLE_PROFILES[settings.level],
    sim: { ...input.skills },
    used: new Set(),
    notBefore: new Map(),
    minutes: input.pace ?? MINUTES,
    retried: new Set(),
    lastPlanned: new Map(),
    problemIndex: new Map(input.problems.map((pr, i) => [pr.slug, i])),
    heldBack: new Map(),
    leftUndone: new Map(),
  };
  const pinnedDay = new Map(pinned.map(({ day, item }) => [item.id, day]));
  const codingTopics = CODING_TOPICS.map((t) => t.id as CodingTopicId);
  // An OA's last week leaves out topics that are both weak and rare: no time to
  // learn one from scratch. Common ones stay even when weak.
  const worth = codingTopics.filter((t) => (p.profile[t]?.weight ?? 0) >= COMMON_WEIGHT || input.skills[t] >= WEAK_SCORE);
  // The weakest topic that comes up a lot: the last two days touch it so the OA isn't the first time
  const common = codingTopics.filter((t) => (p.profile[t]?.weight ?? 0) >= COMMON_WEIGHT);
  const weakest = [...(common.length ? common : codingTopics)].sort(
    (a, b) => input.skills[a] - input.skills[b] || (p.profile[b]?.weight ?? 0) - (p.profile[a]?.weight ?? 0)
  )[0];

  // Build sessions already on the calendar, per OA, so the next one picks up after them
  const builds = new Map<string, number>();
  for (const item of [...Object.values(frozen).flat(), ...pinned.map((x) => x.item)]) {
    if (item.kind === "build" && item.eventId) builds.set(item.eventId, (builds.get(item.eventId) ?? 0) + 1);
  }
  /** The next build session for a build-in-stages OA: two levels of a system */
  const buildItem = (event: PrepEvent, day: string): PlanItem => {
    const n = builds.get(event.id) ?? 0;
    builds.set(event.id, n + 1);
    const { system, from } = BUILD_SESSIONS[n % BUILD_SESSIONS.length];
    return {
      id: `${day}:build:${event.id}:${system.id}:${from}`,
      kind: "build",
      title: `${system.name} · levels ${from}–${from + 1}`,
      minutes: BUILD_MINUTES,
      reason: `${event.company}'s OA builds one system in stages · ${system.levels[from - 1]}, then ${system.levels[from]}`,
      eventId: event.id,
    };
  };

  // Everything already on the calendar is taken, and from today on counts as
  // practiced ahead (a past day's problem only counts once it's logged, in
  // skills), except what the user set aside: a skipped problem stays taken,
  // one put off with "Not now" is free again LATER_DAYS on. A problem left
  // undone on a past day isn't taken either, so it comes back.
  let lastStory: string | null = null;
  const setAside = new Map((input.setAside ?? []).map((s) => [s.id, s.back]));
  const planned = [
    ...Object.entries(frozen).flatMap(([day, items]) => items.map((item) => ({ day, item }))),
    ...pinned.map(({ day, item }) => ({ day, item })),
  ];
  const done = new Set(input.done ?? []);
  const undone = (day: string, item: PlanItem) =>
    Boolean(item.slug) &&
    (pinnedDay.get(item.id) ?? day) < today &&
    !setAside.has(item.id) &&
    !done.has(item.id) &&
    !input.practice[item.slug!];
  for (const { day, item } of planned) {
    const back = setAside.get(item.id);
    if (undone(day, item)) {
      const on = pinnedDay.get(item.id) ?? day;
      if (on > (p.leftUndone.get(item.slug!) ?? "")) p.leftUndone.set(item.slug!, on);
    } else if (item.slug && back) {
      const from = addDays(day, LATER_DAYS);
      if (from > (p.notBefore.get(item.slug) ?? "")) p.notBefore.set(item.slug, from);
    } else if (item.slug) p.used.add(item.slug);
    if (back === undefined && item.topic && day >= today && !(item.slug && input.practice[item.slug])) p.sim[item.topic] += EXPECTED_GAIN;
  }
  for (const t of codingTopics) if (locked(p, t)) p.heldBack.set(t, waitingOn(t, p.sim));
  // A problem that's on the calendar somewhere else is taken, even if it was put off once
  for (const { day, item } of planned) {
    if (item.slug && setAside.get(item.id) === undefined && !undone(day, item)) p.notBefore.delete(item.slug);
  }
  for (const [day, items] of [...Object.entries(frozen), ...pinned.map((x) => [x.day, [x.item]] as const)]) {
    if (items.some((i) => i.kind === "story") && (!lastStory || day > lastStory)) lastStory = day;
    for (const { topic } of items) {
      if (topic && day > (p.lastPlanned.get(topic) ?? "")) p.lastPlanned.set(topic, day);
    }
  }

  // Goal steps on today's list or pinned to a day are planned already; ones
  // left on past days come back
  const placed = new Set([...(frozen[today] ?? []), ...pinned.map((x) => x.item)].map((i) => i.id));
  const paces: GoalPace[] = (input.goals ?? [])
    .flatMap((goal) => {
      const steps = goal.steps.filter((step) => !placed.has(goalItemId(goal.id, step.id)));
      if (!steps.length || goal.stepMinutes <= 0) return [];
      const open = { ...goal, steps };
      return [{ goal: open, next: 0, perDay: goalPace(open, steps.length, input), owed: 0 }];
    })
    .sort((a, b) => b.perDay - a.perDay || a.goal.id.localeCompare(b.goal.id));
  // The most pressed goal starts on the next day with time, the others a beat
  // apart, so they don't all land on one day
  paces.forEach((pace, i) => (pace.owed = 1 - pace.perDay * (1 + i)));

  const plan: Plan = {};
  for (let d = 0; d < horizon; d++) {
    const day = addDays(today, d);
    const here = pinned.filter((x) => x.day === day).map((x) => x.item);
    // OAs, nearest first
    const active = events
      .map((event) => ({ event, left: daysBetween(day, takeDay(event, today)) }))
      .sort((a, b) => a.left - b.left);
    const oaToday = active.find((a) => a.left === 0);
    // What an OA puts on a day whatever else is there: the debrief after it, and the OA itself
    const fixed: PlanItem[] = [];
    for (const { event, left } of active) {
      if (left < 0 && !event.debriefed && (left === -1 || d === 0)) {
        fixed.push({
          id: `${day}:debrief:${event.id}`,
          kind: "debrief",
          title: `How was the ${event.company} OA?`,
          minutes: DEBRIEF_MINUTES,
          reason: "Three taps, and your plan learns what to work on",
          eventId: event.id,
        });
      }
    }
    if (oaToday) {
      fixed.push({
        id: `${day}:oa:${oaToday.event.id}`,
        kind: "oa",
        title: `${oaToday.event.company} OA`,
        minutes: oaToday.event.kind === "gca" ? GCA_MINUTES : OA_MINUTES,
        reason: oaToday.event.dueKnown
          ? `Due ${dayLabel(oaToday.event.due)}, so take it today`
          : "Aiming for today, no deadline was given",
        eventId: oaToday.event.id,
      });
    }

    if (frozen[day]) {
      const kept = frozen[day].filter((i) => (pinnedDay.get(i.id) ?? day) === day);
      const shown = [...kept, ...here.filter((i) => !kept.some((k) => k.id === i.id))];
      // An OA that came up after today's list was made still shows on it
      plan[day] = [...shown, ...fixed.filter((i) => !shown.some((k) => k.id === i.id))];
      continue;
    }

    const items: PlanItem[] = [...here];
    const dayTopics = new Set<TopicId>(here.flatMap((i) => (i.topic ? [i.topic] : [])));
    const free = minutesOn(settings, day) === 0;
    // Moving something off a day means there's no time for it there: the day
    // keeps that gap rather than filling it with something else
    const movedOff = spent(pinned.filter((x) => x.from === day).map((x) => x.item));
    let budget = Math.max(0, minutesOn(settings, day) - spent(here) - movedOff);

    const add = (item: PlanItem) => {
      if (!items.some((i) => i.id === item.id)) items.push(item);
    };
    fixed.forEach(add);
    if (oaToday) {
      plan[day] = items;
      continue;
    }

    const upcoming = active.filter((a) => a.left > 0);
    const mock = upcoming.find((a) => mockDay(a.event, input) === day);
    if (mock) {
      add({ id: `${day}:mock:${mock.event.id}`, kind: "mock", eventId: mock.event.id, ...mockFor(mock.event, builds.get(mock.event.id) ?? 0) });
      plan[day] = items;
      continue;
    }

    const light = upcoming.find((a) => oaMode(a.left) === "light");
    if (light) {
      if (!free) {
        // Two days out: a full day sharpening what they know. The day before: a
        // short warm-up. Both start on the weakest common topic, so they don't go in blind.
        const { event, left } = light;
        const when = left === 1 ? "tomorrow" : "in 2 days";
        let room = left === 1 ? Math.min(budget, WARMUP_MINUTES) - PLATFORM_MINUTES : budget;
        // A build-in-stages OA keeps one build session on the sharpen day, first: it's what that OA is
        if (left === 2 && event.kind === "icf" && room >= BUILD_MINUTES - SLACK) {
          add(buildItem(event, day));
          room -= BUILD_MINUTES;
        }
        for (let n = 0; n < WEAKEST_PICKS && weakest; n++) {
          const picked = pickProblem(p, weakest as CodingTopicId, day, room);
          if (!picked) break;
          add(
            placeProblem(p, picked, day, event, {
              reason: `${event.company} OA ${when} · ${topicName(weakest)} is your weakest common topic, so you don't go in blind`,
              blindReason: `${event.company} OA ${when} · your weak spot, so you don't go in blind`,
            })
          );
          room -= p.minutes[picked.difficulty];
        }
        if (weakest) dayTopics.add(weakest);
        if (left === 2) items.push(...fillProblems(p, day, room, dayTopics, worth.length ? worth : codingTopics, event));
        if (left === 1) {
          add({
            id: `${day}:platform:${event.id}`,
            kind: "platform",
            title: "Check the OA link and platform",
            minutes: PLATFORM_MINUTES,
            reason: "Open the link, test your setup, pick a quiet time",
            eventId: event.id,
          });
        }
      }
      plan[day] = items;
      continue;
    }

    // Ramp and focus days: OA prep takes its share, split by how close each OA is
    const prepping = upcoming;
    const share = prepping.length ? Math.min(...prepping.map((a) => EVERYDAY_SHARE[oaMode(a.left)])) : 1;
    let oaBudget = round5(budget * (1 - share));
    budget -= oaBudget;
    const urgency = prepping.map((a) => 1 / a.left);
    const totalUrgency = urgency.reduce((a, b) => a + b, 0);
    prepping.forEach(({ event, left }, i) => {
      const mine = i === prepping.length - 1 ? oaBudget : round5((oaBudget * urgency[i]) / totalUrgency);
      oaBudget -= mine;
      const topics = oaMode(left) === "focus" && worth.length ? worth : codingTopics;
      const prep: PlanItem[] = [];
      // A build-in-stages OA practices building, a session a day, before problems
      if (event.kind === "icf" && mine >= BUILD_MINUTES - SLACK) prep.push(buildItem(event, day));
      // What earlier candidates here got stuck on comes first
      const crowd = (event.crowd?.topics ?? []).filter((t) => codingTopics.includes(t));
      if (crowd.length) prep.push(...fillProblems(p, day, round5((mine - spent(prep)) * CROWD_SHARE), dayTopics, crowd, event));
      prep.push(...fillProblems(p, day, mine - spent(prep), dayTopics, topics, event));
      items.push(...prep);
      // What it didn't use goes to everyday practice
      budget += mine - spent(prep);
    });

    // A long day mixes things up instead of being all new problems
    const long = !free && minutesOn(settings, day) >= LONG_DAY;

    // Goals, on days no OA is close, up to half the day
    if (!free && !prepping.length) {
      const room = minutesOn(settings, day);
      const steps = goalItems(paces, day, Math.min(budget, round5(room * GOAL_SHARE)), budget, long);
      items.push(...steps);
      budget -= spent(steps);
    }

    // Everyday: a story about once a week (more often on long days), then problems
    const sinceStory = lastStory ? daysBetween(lastStory, day) : Infinity;
    const storyDue = sinceStory >= STORY_EVERY_DAYS || (long && sinceStory >= LONG_STORY_DAYS);
    if (budget >= STORY_MINUTES && storyDue && !prepping.some((a) => oaMode(a.left) === "focus")) {
      const story = storyItem(p, day);
      add(story);
      dayTopics.add(story.topic!);
      budget -= STORY_MINUTES;
      lastStory = day;
    }
    if (long && budget >= REVIEW_MINUTES && Object.keys(input.practice).length) {
      add({
        id: `${day}:review`,
        kind: "review",
        title: "Review what you solved this week",
        minutes: REVIEW_MINUTES,
        reason: "A long day: go back over this week’s problems before new ones",
      });
      budget -= REVIEW_MINUTES;
    }
    items.push(...fillProblems(p, day, budget, dayTopics, codingTopics, undefined, long ? LONG_RETRIES : 1));
    plan[day] = items;
  }
  return plan;
}
